const Solutions = () => {
  const solutions = [
    {
      title: "Enterprise Automation",
      description: "End-to-end automation solutions for large-scale enterprise operations.",
      icon: (
        <div className="w-12 h-12 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="16" rx="2" stroke="white" strokeWidth="2" fill="none"/>
            <rect x="6" y="8" width="12" height="2" fill="white"/>
            <rect x="6" y="12" width="8" height="2" fill="white"/>
            <rect x="6" y="16" width="10" height="2" fill="white"/>
            <circle cx="18" cy="6" r="2" fill="white"/>
          </svg>
        </div>
      ),
      benefits: ["50% faster processing", "99.9% accuracy", "24/7 operation", "Scalable architecture"],
      industries: ["Manufacturing", "Finance", "Healthcare", "Logistics"],
      image: "/api/placeholder/600/400"
    },
    {
      title: "Customer Service AI",
      description: "Intelligent customer service agents that provide human-like interactions.",
      icon: (
        <div className="w-12 h-12 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 18v-6a9 9 0 0 1 18 0v6" stroke="white" strokeWidth="2" fill="none"/>
            <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z" stroke="white" strokeWidth="2" fill="none"/>
            <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" stroke="white" strokeWidth="2" fill="none"/>
          </svg>
        </div>
      ),
      benefits: ["80% faster resolution", "Multilingual support", "24/7 availability", "Cost reduction"],
      industries: ["Retail", "E-commerce", "SaaS", "Telecommunications"],
      image: "/api/placeholder/600/400"
    },
    {
      title: "Financial Intelligence",
      description: "AI-powered financial analysis and automated compliance monitoring.",
      icon: (
        <div className="w-12 h-12 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="12" y1="1" x2="12" y2="23" stroke="white" strokeWidth="2"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="white" strokeWidth="2" fill="none"/>
          </svg>
        </div>
      ),
      benefits: ["Real-time analysis", "Risk detection", "Compliance automation", "Fraud prevention"],
      industries: ["Banking", "Insurance", "Investment", "Fintech"],
      image: "/api/placeholder/600/400"
    },
    {
      title: "Supply Chain Optimization",
      description: "Intelligent supply chain management with predictive analytics.",
      icon: (
        <div className="w-12 h-12 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="white" strokeWidth="2" fill="none"/>
            <polyline points="3.27,6.96 12,12.01 20.73,6.96" stroke="white" strokeWidth="2" fill="none"/>
            <line x1="12" y1="22.08" x2="12" y2="12" stroke="white" strokeWidth="2"/>
          </svg>
        </div>
      ),
      benefits: ["Demand forecasting", "Inventory optimization", "Route planning", "Cost reduction"],
      industries: ["Retail", "Manufacturing", "Logistics", "E-commerce"],
      image: "/api/placeholder/600/400"
    }
  ]

  const caseStudies = [
    {
      company: "TechCorp Industries",
      challenge: "Manual data processing causing 3-day delays",
      solution: "Custom AI agents for automated data validation and processing",
      results: "90% reduction in processing time, 99.5% accuracy rate"
    },
    {
      company: "Global Retail Chain",
      challenge: "Customer service overwhelmed with 10,000+ daily inquiries",
      solution: "Multilingual AI customer service agents",
      results: "75% of inquiries automated, 40% cost reduction"
    },
    {
      company: "Financial Services Firm",
      challenge: "Manual compliance reporting taking 40 hours weekly",
      solution: "AI-powered compliance monitoring and reporting",
      results: "5-minute automated reports, 100% compliance rate"
    }
  ]

  return (
    <div className="solutions-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('/assets/testbackgroundoneai2.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blue-600/80 text-white rounded-full text-sm font-semibold mb-8 backdrop-blur-sm">
              AI Solutions
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
              Intelligent Solutions{' '}
              <span className="block">
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  For Every Industry
                </span>
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-200 leading-relaxed mb-8 max-w-3xl mx-auto drop-shadow-sm">
              Discover how our AI solutions are transforming businesses across industries, 
              delivering measurable results and competitive advantages.
            </p>
            
            <a 
              href="/contact" 
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span className="mr-3">Explore Solutions</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-7-7l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Industry-Specific{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored AI solutions designed to address the unique challenges of your industry.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
            {solutions.map((solution, index) => (
              <div 
                key={index}
                className="solution-card bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="mr-4">{solution.icon}</div>
                    <h3 className="text-2xl font-bold text-gray-900">{solution.title}</h3>
                  </div>
                  
                  <p className="text-gray-600 mb-6 text-lg">{solution.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Benefits:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {solution.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center text-sm text-gray-700">
                          <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Industries:</h4>
                    <div className="flex flex-wrap gap-2">
                      {solution.industries.map((industry, idx) => (
                        <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                          {industry}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <a 
                    href="/contact" 
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300 text-center block"
                  >
                    Learn More
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Success{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Stories
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real businesses who transformed their operations with our AI solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{study.company}</h3>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-red-600 mb-2">Challenge:</h4>
                  <p className="text-gray-600 text-sm">{study.challenge}</p>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-blue-600 mb-2">Solution:</h4>
                  <p className="text-gray-600 text-sm">{study.solution}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-green-600 mb-2">Results:</h4>
                  <p className="text-gray-600 text-sm">{study.results}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a 
              href="/case-studies" 
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              View All Case Studies
              <svg className="w-5 h-5 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-7-7l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Powered by{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Cutting-Edge Technology
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our solutions leverage the latest AI technologies and frameworks for maximum performance and reliability.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 max-w-4xl mx-auto">
            {["Python", "TensorFlow", "PyTorch", "OpenAI", "AWS", "Azure", "Docker", "Kubernetes", "React", "Node.js", "PostgreSQL", "Redis"].map((tech, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-semibold text-gray-600">{tech.slice(0, 2)}</span>
                </div>
                <span className="text-sm font-medium text-gray-700">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Find Your Solution?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Let's discuss how our AI solutions can address your specific business challenges.
            </p>
            <a 
              href="/contact" 
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Get Custom Solution
              <svg className="w-5 h-5 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-7-7l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Solutions
